/**
 * ConfirmModal Component (WhatsApp)
 *
 * Confirmation dialog shown before firing off a WhatsApp campaign.
 * Surfaces recipient count, template name, and estimated cost so the
 * marketer can sanity-check before committing real money.
 */

import { AlertTriangle, Send, X, DollarSign } from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";

interface ConfirmModalProps {
  recipientCount: number;
  segmentName: string;
  templateName: string;
  estimatedCostUSD: number;
  onConfirm: () => void;
  onCancel: () => void;
  sending: boolean;
}

export function ConfirmModal({
  recipientCount,
  segmentName,
  templateName,
  estimatedCostUSD,
  onConfirm,
  onCancel,
  sending,
}: ConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Confirmar envío de WhatsApp"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-lg text-slate-900">
            Confirmar Envío WhatsApp
          </h3>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={22} className="text-amber-600" />
            </div>
            <div>
              <p className="text-slate-900 font-medium mb-1">
                Confirmá el envío masivo
              </p>
              <p className="text-slate-500 text-sm leading-relaxed">
                Una vez enviado no se puede deshacer. Los destinatarios reciben
                el mensaje al instante desde la cuenta vinculada.
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2.5 mb-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Segmento</span>
              <span className="font-semibold text-emerald-600 truncate max-w-[200px]">
                {segmentName}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Template</span>
              <span className="font-mono text-slate-700 truncate max-w-[200px]">
                {templateName}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Destinatarios</span>
              <span className="font-bold text-slate-900">
                {recipientCount.toLocaleString()} personas
              </span>
            </div>
          </div>

          {/* Cost highlight */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <DollarSign size={14} className="text-emerald-700" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Costo estimado</p>
                <p className="text-[10px] text-slate-400">USD · tarifa Meta</p>
              </div>
            </div>
            <span className="text-2xl font-bold text-emerald-700">
              ${estimatedCostUSD.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={sending}
            className="flex-1"
          >
            Cancelar
          </Button>
          <button
            onClick={onConfirm}
            disabled={sending}
            className={`
              flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300
              ${
                sending
                  ? "bg-emerald-400 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] shadow-lg shadow-emerald-500/25"
              }
              text-white
            `}
          >
            <Send size={15} />
            {sending ? "Enviando..." : "Confirmar Envío"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
